// ============================================
// SOCKET CONTEXT - FASE 15 (MEJORADO)
// ============================================
// MEJORAS:
// - Reconexión automática
// - Mejor manejo de estado de conexión
// - Logging mejorado
// - Cleanup correcto

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    // Retornar objeto por defecto si no hay provider
    // Permite que componentes funcionen temporalmente sin SocketProvider
    return { socket: null, isConnected: false };
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const { user } = useAuth();
  
  // Ref para evitar múltiples conexiones
  const socketRef = useRef(null);
  const isConnectingRef = useRef(false);

  // Función para crear conexión
  const createConnection = useCallback(() => {
    if (!user || isConnectingRef.current) return null;
    
    isConnectingRef.current = true;

    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    const SOCKET_URL = API_URL.replace('/api', '');
    
    console.log('🔌 Intentando conectar Socket.io a:', SOCKET_URL);

    const newSocket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      auth: {
        userId: user.id,
        clienteId: user.clienteId || user.cliente?.id || user.cliente_id,
        canalConfigIds: user.canalConfigIds || [],
        rol: user.rol
      },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000
    });

    // Event: Conectado
    newSocket.on('connect', () => {
      console.log('✅ Socket.io conectado:', newSocket.id);
      setIsConnected(true);
      setReconnectAttempts(0);
      isConnectingRef.current = false;
    });

    // Event: Desconectado
    newSocket.on('disconnect', (reason) => {
      console.log('❌ Socket.io desconectado:', reason);
      setIsConnected(false);
      
      // Si fue desconectado por el servidor, intentar reconectar manualmente
      if (reason === 'io server disconnect') {
        console.log('🔄 Servidor desconectó, intentando reconectar...');
        newSocket.connect();
      }
    });

    // Event: Error de conexión
    newSocket.on('connect_error', (error) => {
      console.error('⚠️ Error de conexión Socket.io:', error.message);
      setIsConnected(false);
      isConnectingRef.current = false;
      setReconnectAttempts(prev => prev + 1);
    });

    // Event: Reconectando
    newSocket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 Intento de reconexión #${attemptNumber}`);
      setReconnectAttempts(attemptNumber);
    });

    // Event: Reconectado
    newSocket.on('reconnect', (attemptNumber) => {
      console.log(`✅ Reconectado después de ${attemptNumber} intentos`);
      setIsConnected(true);
      setReconnectAttempts(0);
    });

    // Event: Falló la reconexión
    newSocket.on('reconnect_failed', () => {
      console.error('❌ Falló la reconexión después de todos los intentos');
      setIsConnected(false);
      isConnectingRef.current = false;
    });

    // Event: Error general
    newSocket.on('error', (error) => {
      console.error('⚠️ Error en Socket.io:', error);
    });

    // Listeners de debug para el chat
    newSocket.on('nuevo_mensaje', (data) => {
      console.log('📨 [SocketContext] Nuevo mensaje recibido');
    });

    newSocket.on('nueva_conversacion', (data) => {
      console.log('💬 [SocketContext] Nueva conversación recibida');
    });

    return newSocket;
  }, [user]);

  // Efecto principal de conexión
  useEffect(() => {
    // Si no hay usuario, desconectar si existe socket
    if (!user) {
      if (socketRef.current) {
        console.log('👤 Usuario deslogueado, desconectando socket...');
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Si ya hay socket conectado para este usuario, no crear otro
    if (socketRef.current && socketRef.current.connected) {
      return;
    }

    // Crear nueva conexión
    const newSocket = createConnection();
    
    if (newSocket) {
      socketRef.current = newSocket;
      setSocket(newSocket);
    }

    // Cleanup al desmontar
    return () => {
      if (socketRef.current) {
        console.log('🧹 Limpiando socket al desmontar SocketProvider');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user, createConnection]);

  // Función para reconectar manualmente
  const reconnect = useCallback(() => {
    if (socketRef.current) {
      console.log('🔄 Reconexión manual solicitada');
      socketRef.current.connect();
    } else if (user) {
      const newSocket = createConnection();
      if (newSocket) {
        socketRef.current = newSocket;
        setSocket(newSocket);
      }
    }
  }, [user, createConnection]);

  // Función para emitir eventos
  const emit = useCallback((event, data) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit(event, data);
      return true;
    }
    console.warn('⚠️ No se puede emitir, socket no conectado');
    return false;
  }, []);

  const value = {
    socket,
    isConnected,
    reconnectAttempts,
    reconnect,
    emit
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
